import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceCreateContractEndpoint,
  useCounterpartServiceGetCounterpartEndpoint,
  useTemplatesServiceCreateSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpointKey,
  useTemplatesServiceUpdateSystemTemplateEndpoint
} from 'src/api/queries'
import { ApiError, CreateCounterpartContractProductDto } from 'src/api/requests'
import {
  FormFooter,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import {
  useFileHandler,
  useScrollToFirstError,
  ValidationError
} from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { AddTemplateContractForm } from '../components'

export type UploadResponse = {
  createdResources: { id: string }[]
}

type CustomCreateCounterpartContractProductDto =
  CreateCounterpartContractProductDto & {
    name: string
    measurementUnitName: string
    tvaUnitRateName: string
    group: string
    tvaTotal: number
    tvaUnitRateId: string
  }

type Props = {
  isCounterpart: boolean
}

export const AddTemplateContract = ({ isCounterpart }: Props) => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const { id, contractId } = useParams()

  const [form] = Form.useForm()

  const { onScrollToError } = useScrollToFirstError(form)
  const queryClient = useQueryClient()

  const { setExistingFiles, onFileChange, saveFiles, isUploading } =
    useFileHandler({
      form,
      folder: `contracts-${id}-template`
    })

  const onMutationSuccess = () => {
    const queryKey = contractId
      ? [
          useTemplatesServiceGetSystemTemplateListEndpointKey,
          String(contractId)
        ]
      : [useTemplatesServiceGetSystemTemplateListEndpointKey]
    queryClient.invalidateQueries({ queryKey })

    onBackClick()
  }

  const { mutate: createContract } =
    useCounterpartServiceCreateContractEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('counterparts.contact_created'))
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { mutate: createTemplate } =
    useTemplatesServiceCreateSystemTemplateEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('counterparts.template_created'))
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { mutate: editTemplate } =
    useTemplatesServiceUpdateSystemTemplateEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('common.save_success'))
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { data: institution } = useCounterpartServiceGetCounterpartEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const { data, isLoading } = useTemplatesServiceGetSystemTemplateEndpoint(
    {
      id: String(contractId)
    },
    [String(contractId)],
    {
      enabled: !!contractId
    }
  )

  const onBackClick = () => {
    form.resetFields()
    navigate(
      isCounterpart
        ? paths.counterpart.details(id)
        : paths.institutions.details(id)
    )
  }

  const onSubmit = async ({
    createNewContract
  }: {
    createNewContract: boolean
  }) => {
    try {
      const values = await form.validateFields()

      const updatedFiles = await saveFiles()

      const items =
        values?.medicine?.map(
          (item: CustomCreateCounterpartContractProductDto) => ({
            stockProductId: item?.stockProductId,
            unitPrice: item?.unitPrice,
            measurementUnitId: item?.measurementUnitId,
            tvaUnitRateId: item?.tvaUnitRateId,
            tvaTotal: item?.tvaTotal,
            quantity: item?.quantity,
            stockProductName: item?.name,
            measurementUnitName: item?.measurementUnitName,
            tvaUnitRateName: item?.tvaUnitRateName,
            group: item?.group
          })
        ) ?? []

      const consumables =
        values?.consumables?.map(
          (item: CustomCreateCounterpartContractProductDto) => ({
            stockProductId: item?.stockProductId,
            unitPrice: item?.unitPrice,
            measurementUnitId: item?.measurementUnitId,
            tvaUnitRateId: item?.tvaUnitRateId,
            tvaTotal: item?.tvaTotal,
            quantity: item?.quantity,
            stockProductName: item?.name,
            measurementUnitName: item?.measurementUnitName,
            tvaUnitRateName: item?.tvaUnitRateName,
            group: item?.group
          })
        ) ?? []

      const payload = {
        beneficiaryId:
          data?.beneficiary?.name === values?.beneficiaryName
            ? data?.beneficiary?.id
            : values?.beneficiaryId,
        counterpartId: String(id),
        contractNumber: values.contractNumber,
        signedOn: dayjs(values.signedOn).format(yearMonthDaySlash),
        startDate: dayjs(values.startDate).format(yearMonthDaySlash),
        endDate: dayjs(values.endDate).format(yearMonthDaySlash),
        description: values.description,
        mediaFilesIds: updatedFiles,
        products: createNewContract ? [...items, ...consumables] : items,
        pharmaceutics: consumables,
        fundingSourceId: values?.fundingSourceId
      }

      if (createNewContract) {
        createContract({
          requestBody: payload
        })
      } else {
        contractId
          ? editTemplate({
              id: String(contractId),
              requestBody: {
                name: values.template_name,
                isPublic: true,
                description: values.template_description,
                content: payload
              }
            })
          : createTemplate({
              requestBody: {
                name: values.template_name,
                isPublic: true,
                description: values.template_description,
                content: payload
              }
            })
      }
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  useEffect(() => {
    if (contractId && data) {
      const templateFileIds = data?.content?.mediaFilesIds?.map(
        (file: AnyObject) => file
      )

      setExistingFiles(templateFileIds)

      form.setFieldsValue({
        template_name: data?.name,
        template_description: data?.description,
        contractNumber: data?.content?.contractNumber,
        description: data?.content?.description,
        signedOn: toDayjs(data?.content?.signedOn),
        startDate: toDayjs(data?.content?.startDate),
        endDate: toDayjs(data?.content?.endDate),
        fundingSourceId: data?.content?.fundingSourceId,
        medicine: data?.content?.products?.map((item: AnyObject) => ({
          name:
            item?.stockProductName ??
            `${item?.stockProduct?.commercialName} ${item?.stockProduct?.doses ?? ''}`,
          producerName: item?.stockProduct?.producer?.name,
          group: item?.stockProduct?.group?.category ?? item?.group,
          stockProductId: item?.stockProduct?.id ?? item?.stockProductId,
          unitPrice: (() => {
            const price = Number(item?.unitPrice) || 0

            return isNaN(price) ? '0.00' : price.toFixed(2)
          })(),
          priceTotal: (() => {
            const quantity = Number(item?.quantity) || 0
            const unitPrice = Number(item?.unitPrice) || 0
            const tvaRate =
              Number(
                item?.tvaUnitRate?.name?.[currentLang?.value]?.replace(
                  '%',
                  ''
                ) || '0'
              ) || 0
            const total = quantity * unitPrice * (1 + tvaRate / 100)

            return isNaN(total) ? '0.00' : total.toFixed(2)
          })(),
          delivered_quantity:
            (item?.quantity ?? 0) - (item?.currentQuantity ?? 0),
          outstanding_quantity: item?.currentQuantity ?? 0,
          measurementUnitId:
            item?.measurementUnit?.id ?? item?.measurementUnitId,
          measurementUnitName:
            item?.measurementUnitName ??
            item?.measurementUnit?.name?.[currentLang?.value],
          tvaTotal: item?.tvaTotal,
          tvaUnitRateName:
            item?.tvaUnitRate?.name?.[currentLang?.value] ??
            item?.tvaUnitRateName,
          tvaUnitRateId: item?.tvaUnitRate?.id ?? item?.tvaUnitRateId,
          quantity: item?.quantity
        })),
        consumables: data?.content?.pharmaceutics?.map((item: AnyObject) => ({
          name:
            item?.stockProductName ??
            `${item?.stockProduct?.commercialName} ${item?.stockProduct?.doses ?? ''}`,
          producerName: item?.stockProduct?.producer?.name,
          group: item?.stockProduct?.group?.category ?? item?.group,
          stockProductId: item?.stockProduct?.id ?? item?.stockProductId,
          unitPrice: (() => {
            const price = Number(item?.unitPrice) || 0

            return isNaN(price) ? '0.00' : price.toFixed(2)
          })(),
          priceTotal: (() => {
            const quantity = Number(item?.quantity) || 0
            const unitPrice = Number(item?.unitPrice) || 0
            const tvaRate =
              Number(
                item?.tvaUnitRate?.name?.[currentLang?.value]?.replace(
                  '%',
                  ''
                ) || '0'
              ) || 0
            const total = quantity * unitPrice * (1 + tvaRate / 100)

            return isNaN(total) ? '0.00' : total.toFixed(2)
          })(),
          delivered_quantity:
            (item?.quantity ?? 0) - (item?.currentQuantity ?? 0),
          outstanding_quantity: item?.currentQuantity ?? 0,
          measurementUnitId:
            item?.measurementUnit?.id ?? item?.measurementUnitId,
          measurementUnitName:
            item?.measurementUnit?.name?.[currentLang?.value] ??
            item?.measurementUnitName,
          tvaTotal: item?.tvaTotal,
          tvaUnitRateName:
            item?.tvaUnitRate?.name?.[currentLang?.value] ??
            item?.tvaUnitRateName,
          tvaUnitRateId: item?.tvaUnitRate?.id ?? item?.tvaUnitRateId,
          quantity: item?.quantity
        })),
        docs: templateFileIds?.map((id: string) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, contractId])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={
        contractId
          ? t('counterparts.draft')
          : t('counterparts.add_new_template_contract')
      }
      items={[
        {
          title: t('counterparts.title'),
          path: paths.counterpart.root
        },
        ...(institution
          ? [
              {
                title: institution?.name ?? '-',
                path: paths.counterpart.details(id)
              }
            ]
          : []),
        {
          title: contractId
            ? t('counterparts.draft')
            : t('counterparts.add_new_template_contract')
        }
      ]}
      extra={
        <SaveConfirm
          title={`${t(contractId ? 'common.save' : 'common.add')}?`}
          onConfirm={() => onSubmit({ createNewContract: true })}
          okButtonProps={{ loading: isLoading || isUploading }}
        >
          <Button type="primary">
            {t('counterparts.create_new_contract_based_on_this_template')}
          </Button>
        </SaveConfirm>
      }
    >
      <Flex
        vertical
        gap={24}
        style={{
          maxHeight: '100%',
          overflowY: 'auto'
        }}
      >
        {contractId && (
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>
        )}

        <AddTemplateContractForm
          form={form}
          data={data}
          onChange={onFileChange}
          isCounterpart={isCounterpart}
        />

        <FormFooter
          onCancel={onBackClick}
          onSave={() => onSubmit({ createNewContract: false })}
          saveConfirmTitle={`${t(contractId ? 'common.save' : 'common.add')}?`}
          saveLabel={t(contractId ? 'common.save' : 'common.add')}
          loading={isLoading || isUploading}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
